import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { canAccessAdminPath } from "@/lib/rbac";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import ForVenues from "@/pages/ForVenues";
import ForPlayers from "@/pages/ForPlayers";
import ForCoaches from "@/pages/ForCoaches";
import Auth from "@/pages/Auth";
import AdminLogin from "@/pages/AdminLogin";
import Search from "@/pages/Search";
import VenueDetail from "@/pages/VenueDetail";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import ReceiptView from "@/pages/ReceiptView";
import MyBookings from "@/pages/MyBookings";
import RevenueCenter from "@/pages/RevenueCenter";
import ProviderDashboard from "@/pages/provider/Dashboard";
import ProviderVenues from "@/pages/provider/Venues";
import NewVenue from "@/pages/provider/NewVenue";
import ProviderBookings from "@/pages/provider/Bookings";
import ProviderEarnings from "@/pages/provider/Earnings";
import CourtManage from "@/pages/provider/CourtManage";
import ProviderVerification from "@/pages/provider/Verification";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProviders from "@/pages/admin/Providers";
import AdminUsers from "@/pages/admin/Users";
import AdminBookings from "@/pages/admin/Bookings";
import AdminVenues from "@/pages/admin/Venues";
import AdminLoginLogs from "@/pages/admin/LoginLogs";
import AdminRevenue from "@/pages/admin/Revenue";
import AdminFinanceBatchReport from "@/pages/admin/AdminFinanceBatchReport";
import AdminSubscriptions from "@/pages/admin/Subscriptions";
import Notifications from "@/pages/Notifications";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import EventRegisterPublic from "@/pages/EventRegisterPublic";
import MyEvents from "@/pages/MyEvents";
import ProviderEvents from "@/pages/provider/Events";
import OrganizeEvent from "@/pages/provider/OrganizeEvent";
import ProviderShareLinks from "@/pages/provider/ShareLinks";
import ProviderAnalytics from "@/pages/provider/Analytics";
import ShareRedirect from "@/pages/ShareRedirect";
import BrowseSessions from "@/pages/BrowseSessions";
import CoachDashboard from "@/pages/coach/Dashboard";
import CoachSessions from "@/pages/coach/Sessions";
import CoachSessionDetail from "@/pages/coach/SessionDetail";
import CoachEarnings from "@/pages/coach/Earnings";
import CoachRatings from "@/pages/coach/Ratings";
import SubscriptionPlans from "@/pages/SubscriptionPlans";
import ProviderCalendar from "@/pages/provider/Calendar";
import CoachCalendar from "@/pages/coach/Calendar";
import CoachEventRequests from "@/pages/coach/EventRequests";
import SPDashboardPage from "@/pages/sp/Dashboard";
import SPEarningsPage from "@/pages/sp/Earnings";
import SPSearchPage from "@/pages/sp/Search";
import SPProfilePage from "@/pages/sp/Profile";
import PlayerCalendar from "@/pages/PlayerCalendar";
import CondoDashboard from "@/pages/condo/Dashboard";
import CondoResidents from "@/pages/condo/Residents";
import CondoRules from "@/pages/condo/Rules";
import CondoReports from "@/pages/condo/Reports";
import FoundingCircle from "@/pages/pilot/FoundingCircle";
import AmbassadorDashboard from "@/pages/pilot/AmbassadorDashboard";
import CFODashboard from "@/pages/admin/CFODashboard";
import ESGManagement from "@/pages/admin/ESGManagement";
import TrustSafety from "@/pages/admin/TrustSafety";
import VenueVerification from "@/pages/admin/VenueVerification";
import VIGFDashboard from "@/pages/admin/VIGFDashboard";
import RecruitmentDashboard from "@/pages/admin/RecruitmentDashboard";
import AdminCoordinators from "@/pages/admin/Coordinators";
import PlatformLogs from "@/pages/admin/PlatformLogs";
import SecurityAlerts from "@/pages/admin/SecurityAlerts";
import StaffUsers from "@/pages/admin/StaffUsers";
import RBACRoles from "@/pages/admin/RBACRoles";
import ApprovalQueue from "@/pages/admin/ApprovalQueue";
import ApprovalHistory from "@/pages/admin/ApprovalHistory";
import CheckerPools from "@/pages/admin/CheckerPools";
import StaffManual from "@/pages/admin/StaffManual";
import Venue360 from "@/pages/admin/Venue360";
import User360 from "@/pages/admin/User360";
import EmailChangeReview from "@/pages/admin/EmailChangeReview";
import KYCReview from "@/pages/admin/KYCReview";
import ProfileSettings from "@/pages/ProfileSettings";
import LGUDashboard from "@/pages/lgu/Dashboard";
import PlayerDashboard from "@/pages/PlayerDashboard";
import BookCourt from "@/pages/BookCourt";
import VenueWizard from "@/pages/VenueWizard";
import FindCoachPage from "@/pages/FindCoach";
import CoordinatorApply from "@/pages/CoordinatorApply";
import NDASign from "@/pages/NDASign";
import BuildTeam from "@/pages/BuildTeam";
import PlayWallet from "@/pages/PlayWallet";
import CoordinatorPipeline from "@/pages/recruitment/Pipeline";
import CoordinatorProspects from "@/pages/recruitment/Prospects";
import CoordinatorKPIs from "@/pages/recruitment/KPIs";
import SpaceManager from "@/pages/provider/SpaceManager";
import VenueSchedule from "@/pages/provider/VenueSchedule";
import MultiSportVenue from "@/pages/MultiSportVenue";
import PaymentPage from "@/pages/PaymentPage";
import BookingReceipt from "@/pages/BookingReceipt";
import JoinBooking from "@/pages/JoinBooking";
import VerifyEmail from "@/pages/VerifyEmail";
import VenueOwnerRegister from "@/pages/VenueOwnerRegister";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SetPassword from "@/pages/SetPassword";
import { I18nProvider } from "@/lib/i18n";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = window.location;
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-court-orange border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function ProviderRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-court-orange border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "PROVIDER") return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-court-orange border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessAdminPath(user, location.pathname)) return <Navigate to="/admin" replace />;
  return children;
}

function ListVenueRedirect() {
  const { user } = useAuth();
  if (user?.role === "PROVIDER") {
    return <Navigate to="/provider/venues?create=1" replace />;
  }
  return <Navigate to="/register-venue" replace />;
}

function CondoRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-court-orange border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "CONDO_ADMIN") return <Navigate to="/" replace />;
  return children;
}

function CoachRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-court-orange border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_coach) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/for-venues" element={<ForVenues />} />
      <Route path="/for-players" element={<ForPlayers />} />
      <Route path="/for-coaches" element={<ForCoaches />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/admin-access" element={<AdminLogin />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/register-venue" element={<VenueOwnerRegister />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/search" element={<Layout><Search /></Layout>} />
      <Route path="/venues/:id" element={<Layout><VenueDetail /></Layout>} />
      <Route path="/checkout/:bookingId" element={<ProtectedRoute><Layout><Checkout /></Layout></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><Layout><PaymentSuccess /></Layout></ProtectedRoute>} />
      <Route path="/receipts/:receiptType/:referenceId" element={<ProtectedRoute><Layout><ReceiptView /></Layout></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><Layout><MyBookings /></Layout></ProtectedRoute>} />
      <Route path="/payment" element={<ProtectedRoute><Layout><PaymentPage /></Layout></ProtectedRoute>} />
      <Route path="/booking-receipt" element={<ProtectedRoute><Layout><BookingReceipt /></Layout></ProtectedRoute>} />
      <Route path="/revenue-center" element={<ProtectedRoute><Layout><RevenueCenter /></Layout></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><PlayerDashboard /></Layout></ProtectedRoute>} />
      <Route path="/book-court" element={<ProtectedRoute><Layout><BookCourt /></Layout></ProtectedRoute>} />
      <Route path="/list-venue" element={<ListVenueRedirect />} />
      <Route path="/find-coach" element={<Layout><FindCoachPage /></Layout>} />
      <Route path="/join-coordinator" element={<CoordinatorApply />} />
      <Route path="/play" element={<ProtectedRoute><Layout><PlayWallet /></Layout></ProtectedRoute>} />
      <Route path="/build-team" element={<ProtectedRoute><Layout><BuildTeam /></Layout></ProtectedRoute>} />
      <Route path="/bookings/calendar" element={<ProtectedRoute><Layout><PlayerCalendar /></Layout></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Layout><Notifications /></Layout></ProtectedRoute>} />
      <Route path="/events" element={<Layout><Events /></Layout>} />
      <Route path="/events/my" element={<ProtectedRoute><Layout><MyEvents /></Layout></ProtectedRoute>} />
      <Route path="/events/:id" element={<Layout><EventDetail /></Layout>} />
      <Route path="/events/:eventId/register" element={<EventRegisterPublic />} />
      <Route path="/sessions" element={<Layout><BrowseSessions /></Layout>} />
      <Route path="/coach" element={<CoachRoute><Layout><CoachDashboard /></Layout></CoachRoute>} />
      <Route path="/coach/sessions" element={<CoachRoute><Layout><CoachSessions /></Layout></CoachRoute>} />
      <Route path="/coach/sessions/:sessionId" element={<Layout><CoachSessionDetail /></Layout>} />
      <Route path="/coach/earnings" element={<CoachRoute><Layout><CoachEarnings /></Layout></CoachRoute>} />
      <Route path="/coach/ratings" element={<CoachRoute><Layout><CoachRatings /></Layout></CoachRoute>} />
      <Route path="/coach/calendar" element={<CoachRoute><Layout><CoachCalendar /></Layout></CoachRoute>} />
      <Route path="/coach/event-requests" element={<CoachRoute><Layout><CoachEventRequests /></Layout></CoachRoute>} />
      <Route path="/sp" element={<ProtectedRoute><Layout><CoachDashboard /></Layout></ProtectedRoute>} />
      <Route path="/sp/profile" element={<ProtectedRoute><Layout><SPDashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/sp/earnings" element={<ProtectedRoute><Layout><SPEarningsPage /></Layout></ProtectedRoute>} />
      <Route path="/sp/search" element={<Layout><SPSearchPage /></Layout>} />
      <Route path="/sp/view/:spId" element={<Layout><SPProfilePage /></Layout>} />
      <Route path="/subscriptions/plans" element={<ProtectedRoute><Layout><SubscriptionPlans /></Layout></ProtectedRoute>} />
      <Route path="/subscriptions/success" element={<ProtectedRoute><Layout><SubscriptionPlans /></Layout></ProtectedRoute>} />
      <Route path="/provider" element={<ProviderRoute><Layout><ProviderDashboard /></Layout></ProviderRoute>} />
      <Route path="/provider/venues" element={<ProviderRoute><Layout><ProviderVenues /></Layout></ProviderRoute>} />
      <Route path="/provider/new-venue" element={<ProviderRoute><Layout><NewVenue /></Layout></ProviderRoute>} />
      <Route path="/provider/bookings" element={<ProviderRoute><Layout><ProviderBookings /></Layout></ProviderRoute>} />
      <Route path="/provider/earnings" element={<ProviderRoute><Layout><ProviderEarnings /></Layout></ProviderRoute>} />
      <Route path="/provider/courts/:resourceId" element={<ProviderRoute><Layout><CourtManage /></Layout></ProviderRoute>} />
      <Route path="/provider/verify/:venueId" element={<ProviderRoute><Layout><ProviderVerification /></Layout></ProviderRoute>} />
      <Route path="/provider/events" element={<ProviderRoute><Layout><ProviderEvents /></Layout></ProviderRoute>} />
      <Route path="/provider/events/create" element={<ProviderRoute><Layout><OrganizeEvent /></Layout></ProviderRoute>} />
      <Route path="/provider/events/:eventId/edit" element={<ProviderRoute><Layout><OrganizeEvent /></Layout></ProviderRoute>} />
      <Route path="/provider/share-links" element={<ProviderRoute><Layout><ProviderShareLinks /></Layout></ProviderRoute>} />
      <Route path="/provider/analytics" element={<ProviderRoute><Layout><ProviderAnalytics /></Layout></ProviderRoute>} />
      <Route path="/provider/calendar" element={<ProviderRoute><Layout><ProviderCalendar /></Layout></ProviderRoute>} />
      <Route path="/provider/spaces/:venueId" element={<ProviderRoute><Layout><SpaceManager /></Layout></ProviderRoute>} />
      <Route path="/admin/venues/:venueId/multisport-engine" element={<AdminRoute><Layout><SpaceManager /></Layout></AdminRoute>} />
      <Route path="/provider/schedule/:venueId" element={<ProviderRoute><Layout><VenueSchedule /></Layout></ProviderRoute>} />
      <Route path="/venues/:id/multisport" element={<Layout><MultiSportVenue /></Layout>} />
      <Route path="/s/:code" element={<ShareRedirect />} />
      <Route path="/join/:shareToken" element={<Layout><JoinBooking /></Layout>} />
      <Route path="/condo" element={<CondoRoute><Layout><CondoDashboard /></Layout></CondoRoute>} />
      <Route path="/condo/residents" element={<CondoRoute><Layout><CondoResidents /></Layout></CondoRoute>} />
      <Route path="/condo/rules" element={<CondoRoute><Layout><CondoRules /></Layout></CondoRoute>} />
      <Route path="/condo/reports" element={<CondoRoute><Layout><CondoReports /></Layout></CondoRoute>} />
      <Route path="/admin" element={<AdminRoute><Layout><AdminDashboard /></Layout></AdminRoute>} />
      <Route path="/admin/providers" element={<AdminRoute><Layout><AdminProviders /></Layout></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><Layout><AdminUsers /></Layout></AdminRoute>} />
      <Route path="/admin/users/:userId/360" element={<AdminRoute><Layout><User360 /></Layout></AdminRoute>} />
      <Route path="/admin/bookings" element={<AdminRoute><Layout><AdminBookings /></Layout></AdminRoute>} />
      <Route path="/admin/venues" element={<AdminRoute><Layout><AdminVenues /></Layout></AdminRoute>} />
      <Route path="/admin/login-logs" element={<AdminRoute><Layout><AdminLoginLogs /></Layout></AdminRoute>} />
      <Route path="/admin/revenue" element={<AdminRoute><Layout><AdminRevenue /></Layout></AdminRoute>} />
      <Route path="/admin/subscriptions" element={<AdminRoute><Layout><AdminSubscriptions /></Layout></AdminRoute>} />
      <Route path="/admin/cfo" element={<AdminRoute><Layout><CFODashboard /></Layout></AdminRoute>} />
      <Route path="/admin/finance/batches/:batchId/report" element={<AdminRoute><Layout><AdminFinanceBatchReport /></Layout></AdminRoute>} />
      <Route path="/admin/esg" element={<AdminRoute><Layout><ESGManagement /></Layout></AdminRoute>} />
      <Route path="/admin/trust" element={<AdminRoute><Layout><TrustSafety /></Layout></AdminRoute>} />
      <Route path="/admin/venue-verification" element={<AdminRoute><Layout><VenueVerification /></Layout></AdminRoute>} />
      <Route path="/admin/venues/:venueId/360" element={<AdminRoute><Layout><Venue360 /></Layout></AdminRoute>} />
      <Route path="/admin/vigf" element={<AdminRoute><Layout><VIGFDashboard /></Layout></AdminRoute>} />
      <Route path="/admin/recruitment" element={<AdminRoute><Layout><RecruitmentDashboard /></Layout></AdminRoute>} />
      <Route path="/admin/coordinators" element={<AdminRoute><Layout><AdminCoordinators /></Layout></AdminRoute>} />
      <Route path="/admin/platform-logs" element={<AdminRoute><Layout><PlatformLogs /></Layout></AdminRoute>} />
      <Route path="/admin/alerts" element={<AdminRoute><Layout><SecurityAlerts /></Layout></AdminRoute>} />
      <Route path="/admin/staff-users" element={<AdminRoute><Layout><StaffUsers /></Layout></AdminRoute>} />
      <Route path="/admin/rbac-roles" element={<AdminRoute><Layout><RBACRoles /></Layout></AdminRoute>} />
      <Route path="/admin/approval-queue" element={<AdminRoute><Layout><ApprovalQueue /></Layout></AdminRoute>} />
      <Route path="/admin/approval-history" element={<AdminRoute><Layout><ApprovalHistory /></Layout></AdminRoute>} />
      <Route path="/admin/email-changes" element={<AdminRoute><Layout><EmailChangeReview /></Layout></AdminRoute>} />
      <Route path="/admin/checker-pools" element={<AdminRoute><Layout><CheckerPools /></Layout></AdminRoute>} />
      <Route path="/admin/staff-manual" element={<AdminRoute><Layout><StaffManual /></Layout></AdminRoute>} />
      <Route path="/admin/kyc-review" element={<AdminRoute><Layout><KYCReview /></Layout></AdminRoute>} />
      <Route path="/recruitment" element={<ProtectedRoute><Layout><CoordinatorPipeline /></Layout></ProtectedRoute>} />
      <Route path="/recruitment/prospects" element={<ProtectedRoute><Layout><CoordinatorProspects /></Layout></ProtectedRoute>} />
      <Route path="/recruitment/kpis" element={<ProtectedRoute><Layout><CoordinatorKPIs /></Layout></ProtectedRoute>} />
      <Route path="/founding" element={<ProtectedRoute><Layout><FoundingCircle /></Layout></ProtectedRoute>} />
      <Route path="/ambassador" element={<ProtectedRoute><Layout><AmbassadorDashboard /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Layout><ProfileSettings /></Layout></ProtectedRoute>} />
      <Route path="/lgu" element={<ProviderRoute><Layout><LGUDashboard /></Layout></ProviderRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
    </I18nProvider>
  );
}
